/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { CheckCircle2, Gavel, ShieldAlert, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import { api } from '../../../api';
import type { AuctionBid, AuctionDetail, BidRejectReason } from '../../../api/areas/market';
import { Badge } from '../../../components/Badge';
import { DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { FactRows, GateList } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { FilterSelect, RefreshButton } from '../../registry/shared';
import { AuctionLinks } from '../module';
import { CustomerRef, marketEnum, money } from '../shared';

/** How many of the latest sessions the page reads the bids of (one request each). */
export const BID_SESSIONS = 20;

type Row = AuctionBid & { auction: AuctionDetail };

const REASONS: BidRejectReason[] = ['AUCTION_NOT_OPEN', 'AUCTION_ENDED', 'BELOW_MINIMUM', 'CURRENCY_MISMATCH', 'NOT_ELIGIBLE', 'INSUFFICIENT_FUNDS'];

async function loadBids(signal: AbortSignal): Promise<{ sessions: number; overridden: number; rows: Row[] }> {
  const list = await api.listAuctions({ limit: 100 }, { signal });
  const recent = list.items.filter((a) => a.status !== 'SCHEDULED').slice(0, BID_SESSIONS);
  const details = await Promise.all(recent.map((a) => api.getAuction(a.id, { signal })));
  const rows = details.flatMap((d) => d.bids.map((b) => ({ ...b, auction: d })));
  rows.sort((x, y) => (x.received_at < y.received_at ? 1 : -1));
  return { sessions: details.length, overridden: details.filter((d) => d.override_approval_id).length, rows };
}

/** `/auctions?tab=bids`, reference 67: every bid of the latest sessions, accepted and rejected, with its reason. */
export function AuctionBidsPage() {
  const { t } = useTranslation('market');
  const [params, setParams] = useSearchParams();
  const outcome = params.get('outcome') ?? '';
  const reason = params.get('reason') ?? '';
  const result = useApi((signal) => loadBids(signal), []);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  const columns: Column<Row>[] = [
    { key: 'at', header: t('au.bids.received'), className: 'whitespace-nowrap text-xs', cell: (b) => formatDateTime(b.received_at) },
    {
      key: 'auction',
      header: t('au.bids.session'),
      cell: (b) => (
        <Link to={`/auctions/${encodeURIComponent(b.auction_id)}`} className="font-semibold text-primary hover:underline">
          {b.auction.domain_name}
        </Link>
      ),
    },
    { key: 'bidder', header: t('auction.bidder'), className: 'max-w-44 whitespace-normal', cell: (b) => <CustomerRef id={b.bidder_customer_id} name={b.bidder_name} /> },
    {
      key: 'amount',
      header: t('auction.amount'),
      numeric: true,
      cell: (b) => <span className={cn('whitespace-nowrap', b.status === 'REJECTED' && 'text-muted-foreground line-through')}>{money(b.amount, b.currency)}</span>,
    },
    {
      key: 'outcome',
      header: t('auction.outcome'),
      cell: (b) =>
        b.status === 'ACCEPTED' ? (
          <Badge tone="ok">{t('auction.bidAccepted')}</Badge>
        ) : (
          <Badge tone="warn" title={b.reject_reason ?? undefined}>
            {t('auction.bidRejected')}
          </Badge>
        ),
    },
    { key: 'reason', header: t('au.bids.reason'), className: 'max-w-48 whitespace-normal text-xs', cell: (b) => (b.reject_reason ? marketEnum('rejectReason', b.reject_reason) : t('au.bids.valid')) },
    { key: 'seq', header: t('auction.seq'), numeric: true, cell: (b) => b.sequence ?? '—' },
  ];

  const header = <PageHeader title={t('au.bids.title')} subtitle={t('au.bids.subtitle', { n: BID_SESSIONS })} actions={<RefreshButton onClick={result.reload} />} />;
  if (result.status !== 'success') {
    return (
      <>
        {header}
        <AuctionLinks here="bids" />
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('auctions.loadError')} /> : <Loading rows={5} />}
      </>
    );
  }
  const all = result.data.rows;
  const accepted = all.filter((b) => b.status === 'ACCEPTED');
  const rejected = all.filter((b) => b.status === 'REJECTED');
  const shown = all.filter((b) => (!outcome || b.status === outcome) && (!reason || b.reject_reason === reason));
  const byReason = REASONS.map((r) => [r, rejected.filter((b) => b.reject_reason === r).length] as const).filter(([, n]) => n > 0);

  return (
    <>
      {header}
      <AuctionLinks here="bids" />
      <div className="space-y-5">
        <StatGrid>
          <StatCard label={t('au.bids.sessions')} icon={Gavel} value={result.data.sessions} hint={t('au.bids.sessionsHint')} />
          <StatCard label={t('au.bids.accepted')} icon={CheckCircle2} tone="ok" value={accepted.length} hint={t('au.bids.acceptedHint')} />
          <StatCard label={t('au.bids.rejected')} icon={XCircle} tone={rejected.length ? 'warn' : 'neutral'} value={rejected.length} hint={t('au.bids.rejectedHint')} />
          <StatCard label={t('au.bids.overridden')} icon={ShieldAlert} value={result.data.overridden} hint={t('au.bids.overriddenHint')} />
        </StatGrid>
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px] [&>*]:min-w-0">
          <div className="min-w-0">
            <FilterBar>
              <FilterSelect
                label={t('auction.outcome')}
                value={outcome}
                onChange={(v) => set('outcome', v)}
                anyLabel={t('any')}
                className="min-w-40"
                options={[
                  { value: 'ACCEPTED', label: t('auction.bidAccepted') },
                  { value: 'REJECTED', label: t('auction.bidRejected') },
                ]}
              />
              <FilterSelect label={t('au.bids.reason')} value={reason} onChange={(v) => set('reason', v)} anyLabel={t('any')} className="min-w-52" options={REASONS.map((r) => ({ value: r, label: marketEnum('rejectReason', r) }))} />
            </FilterBar>
            <Section flush title={t('au.bids.list')} description={t('au.bids.listHint')}>
              <DataTable columns={columns} rows={shown} rowKey={(b) => b.id} empty={<EmptyState title={t('au.bids.none')} />} />
            </Section>
          </div>
          <div className="min-w-0 space-y-5">
            <Section title={t('au.bids.reasonsTitle')}>
              {byReason.length ? <FactRows rows={byReason.map(([r, n]) => [marketEnum('rejectReason', r), n])} /> : <p className="text-sm text-muted-foreground">{t('au.bids.noRejects')}</p>}
            </Section>
            <Section title={t('au.bids.guardsTitle')}>
              <GateList
                gates={[
                  { id: 'seq', title: t('au.bids.guard.seq'), hint: t('au.bids.guard.seqHint'), ok: null },
                  { id: 'rejects', title: t('au.bids.guard.rejects'), hint: t('au.bids.guard.rejectsHint'), ok: null },
                  { id: 'funds', title: t('au.bids.guard.funds'), hint: t('au.bids.guard.fundsHint'), ok: null },
                  { id: 'override', title: t('au.bids.guard.override'), hint: t('au.bids.guard.overrideHint'), ok: null },
                ]}
              />
            </Section>
          </div>
        </div>
      </div>
    </>
  );
}
